<template>
  <div :style="[styles.container, customStyle, sizeStyle]">
    <img
      v-if="srcComputed"
      :style="[{
                 flex: 1,
                 backgroundColor: 'transparent',
                 objectFit: 'cover',
                 borderRadius: customStyle && customStyle.borderRadius || 0,
               },
               imgBrowserStyle,
      ]"
      :resizeMode="resizeMode"
      :src="srcComputed | https"
      @load="onImageLoad"
      @loadStart="onImageLoadStart"
      @loadEnd="onImageLoadEnd"
      @error="onImageError"
    >
    <img
      v-if="isShowError"
      :style="{ position: 'absolute',backgroundColor: 'transparent', top: 0, bottom: 0, left: 0, right: 0 }"
      :resizeMode="'cover'"
      :src="getStatusImage | https"
    >
  </div>
</template>

<script>
import StyleSheet from '@/components/basic/StyleSheet';
import { isNative, isBrowser } from '@/utils/env';
import imageLoading from './img/image_loading.png';
import imageLoadingHor from './img/image_loading_hor.png';
import imageLoadingVer from './img/image_loading_ver.png';
import imageFail from './img/image_fail.png';
import imageFailHor from './img/image_fail_hor.png';
import imageFailVer from './img/image_fail_ver.png';

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    collapsable: false,
    overflow: 'hidden',
  },
});

const IMAGE_LOAD_STATUS_DEGAULT = 0;
const IMAGE_LOAD_STATUS_LOADING = 1;
const IMAGE_LOAD_STATUS_SUCCESS = 2;
const IMAGE_LOAD_STATUS_FAIL = 3;

export default {
  name: 'HyImage',
  filters: {
    https(url) {
      if (url.indexOf('http://') === 0) {
        return url.replace('http://', 'https://');
      }
      return url;
    },
  },
  props: {
    src: {
      type: String,
      default: '',
    },
    placehold: {
      type: String,
      default: '',
    },
    resizeMode: {
      type: String,
      default: 'cover',
    },
    loadingImg: {
      type: String,
      default: '',
    },
    width: {
      type: [Number, String],
      default: 0,
    },
    height: {
      type: [Number, String],
      default: 0,
    },
    customStyle: {
      type: Object,
      default: () => {
      },
    },
  },
  data() {
    return {
      styles,
      status: IMAGE_LOAD_STATUS_DEGAULT,
      cdnDomains: [
        /^https?:\/\/static.gameplus.qq.com/,
        /^https?:\/\/test-static.gameplus.qq.com/,
        /^https?:\/\/gameplus-test-1258344700/,
        /^https?:\/\/gameplus-1258344700/,
      ],
      sizeRange: [80, 160, 320, 480, 640, 720, 800, 1080, 1440],
      elWidth: undefined,
      elHeight: undefined,
      isBrowser: isBrowser(),
    };
  },
  computed: {
    boxWidth() {
      const width = (this.elWidth || this.width || this.customStyle?.width || '').toString().replace(/(\d*).*/g, '$1');
      return Number(width);
    },
    boxHeight() {
      const height = (this.elHeight || this.height || this.customStyle?.height || '').toString().replace(/(\d*).*/g, '$1');
      return Number(height);
    },
    srcComputed() {
      let { src } = this;
      let size = this.reduceSize;
      // 域名支持图片处理
      if (this.canReduce) {
        if (!size) return '';
        // 有大小，可以按阶段进行裁剪处理
        for (let i = 0, len = this.sizeRange.length; i < len; i++) {
          if (size <= this.sizeRange[i]) {
            size = this.sizeRange[i];
            break;
          }
        }
        src = `${src}?imageView2/2/w/${size}`;
        // 如果是Android，可以加上webp格式参数
        if (this.isAndroid()) {
          if (src.indexOf('?') !== -1) {
            src = `${src}|imageMogr2/format/webp|`;
          } else {
            src = `${src}?imageMogr2/format/webp|`;
          }
        }
      }
      return src.replace(/^http(?=:)/, 'https');
    },
    reduceSize() {
      return this.boxWidth * 2;
    },
    sizeStyle() {
      const sizeObj = {};
      if (this.width > 0 && typeof this.width === 'number') {
        sizeObj.width = this.calcNum(this.width);
      } else if (!isNative && this.width !== 0) {
        sizeObj.width = this.width;
      }
      if (this.height > 0 && typeof this.height === 'number') {
        sizeObj.height = this.calcNum(this.height);
      } else if (!isNative && this.height !== 0) {
        sizeObj.height = this.height;
      }
      return sizeObj;
    },
    isShowImage() {
      return this.status === IMAGE_LOAD_STATUS_DEGAULT || this.status === IMAGE_LOAD_STATUS_SUCCESS;
    },
    isShowError() {
      return this.status === IMAGE_LOAD_STATUS_FAIL;
    },
    getStatusImage() {
      const imageWidth = this.boxWidth;
      const imageHeight = this.boxHeight;
      if (this.status === IMAGE_LOAD_STATUS_LOADING) {
        if (this.loadingImg.length > 0) {
          return this.loadingImg;
        }
        if (imageHeight > 0) {
          if (imageWidth / imageHeight > 672 / 504) {
            return imageLoadingHor;
          }
          if (imageWidth / imageHeight < 504 / 672) {
            return imageLoadingVer;
          }
        }
        return imageLoading;
      }
      if (this.status === IMAGE_LOAD_STATUS_FAIL) {
        if (imageHeight > 0) {
          if (imageWidth / imageHeight > 672 / 504) {
            return this.placehold.length > 0 ? this.placehold : imageFailHor;
          }
          if (imageWidth / imageHeight < 504 / 672) {
            return this.placehold.length > 0 ? this.placehold : imageFailVer;
          }
        }
      }
      return this.placehold.length > 0 ? this.placehold : imageFail;
    },
    canReduce() {
      for (let i = 0, len = this.cdnDomains.length; i < len; i++) {
        if (this.cdnDomains[i].test(this.src)) {
          return true;
        }
      }
      return false;
    },
    imgBrowserStyle() {
      if (!this.isBrowser) return {};
      return {
        width: '100%',
        height: '100%',
      };
    },
  },
  mounted() {
    if (this.isBrowser) {
      this.elWidth = this?.$el?.clientWidth;
      this.elHeight = this?.$el?.clientHeight;
    } else {
      this.elWidth = this?.$el?.style?.width;
      this.elHeight = this?.$el?.style?.height;
    }

    if (!this.boxWidth || !this.boxHeight) {
      this.$el.addEventListener('layout', this.handleImageBoxLayout);
    }
  },
  methods: {
    onImageLoad() {
      // this.status = IMAGE_LOAD_STATUS_SUCCESS;
    },
    onImageLoadStart() {
      // 这里加上loading状态会有灰色-闪烁情况
      // this.status = IMAGE_LOAD_STATUS_LOADING;
    },
    onImageLoadEnd() {
    },
    onImageError() {
      this.status = IMAGE_LOAD_STATUS_FAIL;
    },
    handleImageBoxLayout(e) {
      if (typeof this.elWidth === 'undefined') {
        this.elWidth = e.width;
      }
      if (typeof this.elHeight === 'undefined') {
        this.elHeight = e.height;
      }
      e.stopPropagation();
    },
  },
};
</script>
